// Sprint 12 (D-008) — chat-header status pill.
//
// Sprint S20.5 PR-4 (D013-EC-70) extends the pill to render the idle
// hint when status === 'waiting'. Before this sprint the pill rendered
// ONLY for `running` (D-008 binary states); founder reported the silent
// idle state as "stuck", so we surface a subtle "Idle · Send a message
// to continue." cue. The two pills are visually distinct (running is
// success-green, idle is muted) so the founder reads them at a glance.
// All other statuses (finished / interrupted / stuck / error) still
// return null — the chat header does not surface terminal states.

import type { AgentStatus } from '../../types';
import { useTranslation } from '../../i18n';

export interface StatusPillProps {
  status: AgentStatus;
  /** Sprint working-signal-ssot — server-derived task-scoped signal.
   * When set, the green Working pill renders on it (stuck-but-alive,
   * open card, background subagent, pending wakeup). Optional so
   * status-only callers keep the pre-sprint running-only mapping. */
  working?: boolean;
  /**
   * Sprint job-tree-four-states (D-028 §2.1) — the four founder states.
   * When present it WINS over `status` and `working`, exactly as it does
   * in `StatusIcon`.
   *
   * THIS IS THE SURFACE THE SPRINT'S OWN ARGUMENT RESTS ON. D6 kills the
   * push on the grounds that "the surface he IS looking at tells the
   * truth", quoting him directly: *"incluso teniendo al agente delante,
   * no sé si ha terminado de trabajar o no, o si se ha quedado atascado
   * o no"*. The sidebar row was updated and the chat header was not, so
   * the one surface named in that quote — the conversation open in front
   * of him — still rendered nothing for `Needs you` and nothing for
   * `Unconfirmed`. Caught by the main reviewer.
   */
  founderState?: 'working' | 'needs-you' | 'done' | 'unconfirmed';
  /** Severity split inside `needs-you` — an unrecoverable stop reads red. */
  severity?: 'normal' | 'unrecoverable';
  /**
   * The rollup's one line of plain language. Rendered VERBATIM: it is
   * already founder copy (it prefers the CLI's humanised tool
   * description and never carries a tool id, per founder-first §3/§8.6).
   * This is what answers "what is it doing right now" without reading
   * the transcript.
   */
  detail?: string | null;
}

const PILL_BASE = [
  'inline-flex items-center shrink-0 px-pad-chip-x py-pad-chip-y',
  'rounded-full text-caption tabular-nums',
].join(' ');


export function StatusPill({
  status,
  working,
  founderState,
  severity = 'normal',
  detail,
}: StatusPillProps) {
  // Rules-of-Hooks: useTranslation must run before any conditional return.
  // Detected by react-hooks/rules-of-hooks lint added in S11b.2 hotfix —
  // the original code put the `status !== 'running'` early return above
  // the hook, which crashes with "rendered fewer hooks than expected" on
  // the running → idle transition the moment React unmounts the pill.
  const { t } = useTranslation();

  // Sprint working-signal-ssot (Gate 2 fix) — error outranks working,
  // matching StatusIcon's precedence: an errored row with a pending
  // wakeup derives working=true (the wakeup will resurrect it), but a
  // green "Working" pill would mask the error. The chat header does not
  // surface terminal states (D-008 §3), so error renders nothing here;
  // the sidebar's red CircleAlert carries the signal.
  // ── The four founder states (D-028) ──────────────────────────────────
  //
  // Placed ABOVE the `status === 'error'` early return below, because that
  // return encodes the pre-sprint conflation this sprint removes: it drops
  // the pill for an errored row so a green "Working" cannot mask the
  // error. With `founderStatus` the pill no longer guesses from `status`
  // at all — an errored row rolls up to `Needs you` (red when its work
  // stopped without finishing), which is MORE informative than silence,
  // and the header stops being the one place that goes blank exactly when
  // something is wrong.
  if (founderState) {
    // `done` RENDERS NOTHING. The header stays silent when nothing is
    // pending, and the composer is the affordance (D-008).
    //
    // FENCE, and the first draft crossed it backwards. Sprint
    // `chat-header-restore-name-drop-idle` (PR #330, D2, 2026-05-24)
    // DELETED this exact string on Santi's verbatim instruction: *"se ha
    // quedado lo que querían quitar, que era lo de idle · send a message
    // to continue"*. Un-gating the pill and giving `done` the muted idle
    // cue put it straight back — and in production `founderStatus` is
    // always present, so it would have appeared on every finished
    // conversation. Caught by the main reviewer; the earlier comment here
    // claimed the old gate was "protecting" the cue when the gate existed
    // to SUPPRESS it.
    //
    // What the sprint actually needed from this component was `Needs you`
    // and `Unconfirmed` becoming visible — the two states he can act on.
    // `done` being silent was never the problem.
    if (founderState === 'done') return null;
    const tone =
      founderState === 'working'
        ? 'bg-status-success/15 text-status-success'
        : founderState === 'unconfirmed'
          ? 'bg-muted/60 text-muted-foreground'
          : severity === 'unrecoverable'
            ? 'bg-status-error/15 text-status-error'
            : 'bg-status-warning/15 text-status-warning';
    const label =
      founderState === 'working'
        ? t('status.running')
        : founderState === 'unconfirmed'
          ? t('status.unconfirmed')
          : t('status.needsYou');
    // The wrapper carries NO pill chrome — only the inner span is a pill.
    // The first draft applied `PILL_BASE` to both, double-padding and
    // double-rounding the composition, and pinned the width with an
    // arbitrary `max-w-[20rem]` that `design-discipline.md` forbids. Both
    // found by the main reviewer; no visual baseline covers this
    // composition, because the mockup story carries no `founderStatus`.
    // Truncation now comes from `min-w-0` + `truncate` and the header's
    // own flex, which is how every other chip in that row behaves.
    return (
      <span
        className="inline-flex items-center gap-1.5 min-w-0"
        data-testid="chat-header-status-pill"
        data-status={status}
        data-founder-state={founderState}
        data-severity={severity}
        data-working={founderState === 'working' ? 'true' : undefined}
        title={detail ?? undefined}
      >
        <span className={`${PILL_BASE} shrink-0 font-550 ${tone}`}>{label}</span>
        {detail ? (
          <span className="truncate text-caption font-450 text-muted-foreground">{detail}</span>
        ) : null}
      </span>
    );
  }

  if (status === 'error') return null;

  if (working ?? status === 'running') {
    return (
      <span
        className={[
          'inline-flex items-center shrink-0 px-pad-chip-x py-pad-chip-y',
          'rounded-full text-caption font-550 tabular-nums',
          'bg-status-success/15 text-status-success',
        ].join(' ')}
        data-testid="chat-header-status-pill"
        data-status={status}
        data-working="true"
      >
        {t('status.running')}
      </span>
    );
  }

  if (status === 'waiting') {
    return (
      <span
        className={[
          'inline-flex items-center shrink-0 px-pad-chip-x py-pad-chip-y',
          'rounded-full text-caption font-450 tabular-nums',
          'bg-muted/60 text-muted-foreground',
        ].join(' ')}
        data-testid="chat-header-status-pill"
        data-status="waiting"
      >
        {t('chat.idleHint')}
      </span>
    );
  }

  return null;
}
